import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CoreConfig, CORE_CONFIG } from '../../models/core-config/core-config.interface';

/**
 * Mapbox API Types
 * Based on https://docs.mapbox.com/api/search/search-box/
 */

export interface MapboxSuggestion {
  name: string;
  mapbox_id: string;
  feature_type: string;
  address: string;
  full_address: string;
  place_formatted: string;
  context: MapboxContext;
  language: string;
  maki: string;
}

export interface MapboxContext {
  country?: MapboxCountry;
  postcode?: MapboxLocality;
  place?: MapboxLocality;
  locality?: MapboxLocality;
  neighborhood?: MapboxLocality;
  address?: MapboxAddress;
  street?: MapboxLocality;
  region?: MapboxRegion;
}

export interface MapboxCountry {
  id: string;
  name: string;
  country_code: string;
  country_code_alpha_3: string;
}

export interface MapboxLocality {
  id: string;
  name: string;
}

export interface MapboxAddress {
  id: string;
  name: string;
  address_number: string;
  street_name: string;
}

export interface MapboxRegion {
  id: string;
  name: string;
  region_code?: string;
  region_code_full: string;
}

export interface MapboxSearchResponse {
  suggestions: MapboxSuggestion[];
  attribution: string;
}

export interface MapboxFeature {
  type: string;
  geometry: {
    coordinates: [number, number];
    type: string;
  };
  properties: {
    name: string;
    mapbox_id: string;
    feature_type: string;
    address: string;
    full_address: string;
    place_formatted: string;
    context: MapboxContext;
    coordinates: {
      latitude: number;
      longitude: number;
      accuracy: string;
      routable_points: Array<{
        name: string;
        latitude: number;
        longitude: number;
      }>;
    };
    language: string;
    maki: string;
  };
}

export interface MapboxRetrieveResponse {
  type: string;
  features: MapboxFeature[];
  attribution: string;
}

export interface MapboxAutocompleteResult {
  title: string;
  value: string;
  suggestion: MapboxSuggestion;
}

export interface MapboxAddressResult {
  address1: string;
  city: string;
  postalCode: string;
  latitude: number;
  longitude: number;
  countryCode?: string;
  country?: string;
  state?: string;
  pathNumber?: string;
  pathName?: string;
}

/**
 * Service pour gérer les appels API Mapbox
 * Inspiré de l'implémentation dans monovue3
 */
@Injectable({
  providedIn: 'root'
})
export class MapboxAddressService {
  private readonly MAPBOX_API_URL = 'https://api.mapbox.com';
  private readonly MAPBOX_ACCESS_TOKEN: string;
  private sessionToken: string;

  constructor(
    private http: HttpClient,
    @Inject(CORE_CONFIG) private config: CoreConfig
  ) {
    this.MAPBOX_ACCESS_TOKEN = (this.config.mapbox && this.config.mapbox.accessToken) || '';
    this.sessionToken = this.generateSessionToken();
  }

  /**
   * Vérifie que le token Mapbox est configuré
   * @throws Error si le token n'est pas configuré
   */
  private checkToken(): void {
    if (!this.MAPBOX_ACCESS_TOKEN) {
      throw new Error('Mapbox access token is not configured in the environment. Please add mapbox.accessToken to your environment configuration.');
    }
  }

  /**
   * Get language code based on country code
   */
  private getLanguageForCountry(countryCode: string): string {
    const languageMap: Record<string, string> = {
      'FR': 'fr',
      'GB': 'en',
      'UK': 'en',
      'US': 'en',
      'ES': 'es',
      'MX': 'es',
      'PT': 'pt',
      'IT': 'it',
      'DE': 'de'
    };
    return languageMap[countryCode] || 'en';
  }

  /**
   * Génère un token de session unique
   */
  private generateSessionToken(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Recherche d'adresses avec suggestions
   * @param query Texte de recherche
   * @param countryCode Code pays ISO (ex: 'FR')
   * @param types Types de features à rechercher
   * @param proximity Coordonnées de proximité pour améliorer les résultats
   */
  search(
    query: string,
    countryCode: string = 'FR',
    types: string[] = ['country', 'region', 'postcode', 'district', 'place', 'locality', 'street', 'address'],
    proximity?: { latitude: number; longitude: number }
  ): Observable<MapboxAutocompleteResult[]> {
    this.checkToken();

    if (!query || query.length < 3) {
      return new Observable(observer => {
        observer.next([]);
        observer.complete();
      });
    }

    const language = this.getLanguageForCountry(countryCode);

    let params = new HttpParams()
      .set('q', query)
      .set('access_token', this.MAPBOX_ACCESS_TOKEN)
      .set('session_token', this.sessionToken)
      .set('limit', '10')
      .set('language', language)
      .set('types', types.join(','))
      .set('country', countryCode);

    if (proximity) {
      params = params.set('proximity', `${proximity.longitude},${proximity.latitude}`);
    }

    const url = `${this.MAPBOX_API_URL}/search/searchbox/v1/suggest`;

    return this.http.get<MapboxSearchResponse>(url, { params }).pipe(
      map(response => this.formatSuggestionsToAutocomplete(response.suggestions))
    );
  }

  /**
   * Récupère les détails complets d'une suggestion
   * @param mapboxId ID de la suggestion Mapbox
   */
  retrieve(mapboxId: string): Observable<MapboxAddressResult> {
    this.checkToken();

    const params = new HttpParams()
      .set('access_token', this.MAPBOX_ACCESS_TOKEN)
      .set('session_token', this.sessionToken);

    const url = `${this.MAPBOX_API_URL}/search/searchbox/v1/retrieve/${mapboxId}`;

    return this.http.get<MapboxRetrieveResponse>(url, { params }).pipe(
      map(response => this.formatRetrieveToAddress(response))
    );
  }

  /**
   * Formate les suggestions en résultats d'autocomplete
   */
  private formatSuggestionsToAutocomplete(suggestions: MapboxSuggestion[]): MapboxAutocompleteResult[] {
    if (!suggestions || suggestions.length === 0) {
      return [];
    }

    return suggestions.map(suggestion => ({
      title: this.getFormattedAddress(suggestion),
      value: suggestion.mapbox_id,
      suggestion
    }));
  }

  /**
   * Formate la réponse retrieve en objet Address
   */
  private formatRetrieveToAddress(response: MapboxRetrieveResponse): MapboxAddressResult {
    if (!response.features || response.features.length === 0) {
      return null;
    }

    const feature = response.features[0];
    const properties = feature.properties;

    // Extract pathNumber and pathName directly from Mapbox context.address
    const contextAddress = properties.context && properties.context.address;
    const contextStreet = properties.context && properties.context.street;
    const pathNumber = (contextAddress && contextAddress.address_number) || undefined;
    const pathName = (contextAddress && contextAddress.street_name) || (contextStreet && contextStreet.name) || undefined;

    return {
      address1: this.getStreetAddress(properties),
      city: this.getCity(properties),
      postalCode: this.getPostalCode(properties),
      latitude: properties.coordinates.latitude,
      longitude: properties.coordinates.longitude,
      countryCode: properties.context.country ? properties.context.country.country_code : undefined,
      country: properties.context.country ? properties.context.country.name : undefined,
      state: this.getState(properties),
      pathNumber,
      pathName
    };
  }

  /**
   * Récupère l'adresse formatée depuis une suggestion ou un feature
   */
  private getFormattedAddress(result: MapboxSuggestion | MapboxFeature['properties']): string {
    if (!result) return '';

    // Si c'est une adresse complète, utiliser full_address
    if (result.full_address) return result.full_address;

    // Sinon, formatter selon le type
    const featureType = result.feature_type;
    if (featureType === 'place') return `${result.name}, ${result.place_formatted}`;
    if (featureType === 'street') return `${result.name}, ${result.place_formatted}`;
    if (featureType === 'postcode') return `${result.name} ${result.place_formatted}`;
    if (featureType === 'region') return `${result.name} ${result.place_formatted}`;

    return result.place_formatted || result.name;
  }

  /**
   * Récupère la ville depuis une suggestion ou un feature
   */
  private getCity(result: MapboxSuggestion | MapboxFeature['properties']): string {
    if (!result) return '';

    // Si c'est une recherche de ville directe
    if (result.feature_type === 'place') return result.name;
    if (result.feature_type === 'region') return result.name;

    return (result.context && result.context.place && result.context.place.name) ? result.context.place.name : '';
  }

  /**
   * Récupère le code postal depuis une suggestion ou un feature
   */
  private getPostalCode(result: MapboxSuggestion | MapboxFeature['properties']): string {
    if (!result) return '';

    // Si c'est une recherche de code postal directe
    if (result.feature_type === 'postcode') return result.name;

    return (result.context && result.context.postcode && result.context.postcode.name) ? result.context.postcode.name : '';
  }

  /**
   * Get state/region from result context
   */
  private getState(result: MapboxSuggestion | MapboxFeature['properties']): string {
    if (!result || !result.context) return '';

    // Extract region from context
    if (result.context.region) {
      return result.context.region.name;
    }

    return '';
  }

  /**
   * Extract only the street address (number + street name) without city and postal code
   */
  private getStreetAddress(result: MapboxSuggestion | MapboxFeature['properties']): string {
    if (!result) return '';

    // For addresses, try to extract number and street name
    if (result.context && result.context.address) {
      const address = result.context.address;
      if (address.address_number && address.street_name) {
        return `${address.address_number} ${address.street_name}`;
      }
    }

    // If we have a street name in the context
    if (result.context && result.context.street) {
      return result.context.street.name;
    }

    // Otherwise, use the address field if it exists (without city and postal code)
    if (result.address) {
      return result.address;
    }

    // As a last resort, use the name
    return result.name || '';
  }
}

